import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DialogPlants from '@/components/DialogPlants/DialogPlants';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ButtonChipMultiple from '@/components/UI/ButtonChipMultiple/ButtonChipMultiple';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import { ALIGN, CELL_EDITOR_TYPES, CELL_RENDERER_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { validatePlant } from '@/helpers/administrationUtils';
import { SELECTION_MODES_FORM, WORKFLOW_TYPE_FORM, errorTypes, formYESNO } from '@/helpers/constants';
import { langFormFormatter } from '@/helpers/tables-utils';
import { getLanguageTranslations } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setLanguagesForm, setPlantsForm } from '@/redux/Forms/forms.action';
import { getLanguagesForm, getPlantsForm } from '@/redux/Forms/forms.reducer';
import administrationServices from '@/services/administrationServices';
import languageService from '@/services/languageService';
import titleService from '@/services/titleService';
import { ColumnConfig, LanguagesForm, Plant, UsersSelectionColumnConfig } from '@/types';
import styles from './Plants.module.scss';

const COLUMN_NAMES = {
  PLANT_NAME: 'plantName',
  WKZ_NUMBER: 'wkzNumber',
  AUTHORIZED_USER_CODES: 'authorizedUserCodes',
  LANGUAGE: 'language',
  MAILBOX: 'mailbox',
  CKD: 'ckd',
  QMB_APPROVER: 'qmbApprover',
  SELECTION_MODE: 'selectionMode',
  WORKFLOW_TYPE: 'workflowType',
  ACTIVE: 'active',
};

const availableFilters = ['all', 'active', 'ckd', 'withQMBApprover'];

const EDITABLE_TEXT_INPUT_COLUMNS = [COLUMN_NAMES.PLANT_NAME, COLUMN_NAMES.MAILBOX];

interface PlantsSection {
  languagesForm: LanguagesForm;
  setLanguages: (lang: LanguagesForm) => void;
  plants: Plant[];
  setPlants: (plants: Plant[]) => void;
}

const Plants: React.FC<PlantsSection> = (props) => {
  const { languagesForm, setLanguages, plants, setPlants } = props;

  const { t } = useTranslation();
  const lang = useAppLanguage();
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();
  const [resultsPlants, setResultsPlants] = useState<Plant[]>([]);
  const [filterSelected, setFilterSelected] = useState('all');
  const [popupOpen, setPopupOpen] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const { addToast } = useToastContext();
  const [errorPlantList, setErrorPlantList] = useState<string[]>([]);

  const currentSelection = useMemo(() => selection?.map((rowId) => resultsPlants
    .find((plant) => plant.wkzNumber === rowId)), [selection, resultsPlants]);
  const showOnlyOnePlantDialog = errorPlantList.length > 0;

  const getMailboxValue = useCallback((mailbox: string[] | string) => Array.isArray(mailbox) ? mailbox[0] : mailbox || '', []);

  const getPlants = useCallback(() => {
    administrationServices.getPlants()
      .then((resPlants) => {
        setPlants(resPlants.plants);
        const multiple = Object.keys(WORKFLOW_TYPE_FORM).includes(filterSelected)
          ? COLUMN_NAMES.WORKFLOW_TYPE
          : Object.keys(languagesForm).includes(filterSelected) ? COLUMN_NAMES.LANGUAGE : null;
        handleFilterChange(filterSelected, multiple, resPlants.plants);
        setSelection([]);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setLoading(false);
        setGlobalLoading(false);
        setPopupOpen('');
      });
  }, [addToast, filterSelected, languagesForm, setGlobalLoading, setPlants]);

  const editPlant = useCallback((changedRows: Plant[], rowEdited: Plant) => {
    setLoading(true);
    setResultsPlants(changedRows);
    administrationServices.editPlant(rowEdited.wkzNumber, rowEdited)
      .then(getPlants)
      .catch((error) => {
        addToast(error.message, 'ERROR');
        setLoading(false);
      });
  }, [addToast, getPlants]);

  const saveEditedRow = useCallback((rowData: unknown, skipValidation = false, columnName?: string, value?: unknown): boolean => {
    const rawRow = rowData as Plant;
    const rowEdited: Plant = {
      ...rawRow,
      mailbox: getMailboxValue(rawRow.mailbox),
      ...(columnName && { [columnName]: value }),
    };
    const changedRows = resultsPlants.map((current) =>
      current.wkzNumber === rowEdited.wkzNumber ? rowEdited : current,
    );

    const isValid = skipValidation || validatePlant(rowEdited, addToast, t);

    if (isValid) {
      editPlant(changedRows, rowEdited);
    }

    return isValid;
  }, [resultsPlants, addToast, t, editPlant, getMailboxValue]);

  const buildUsersSelection = useCallback((
    title: string,
    config?: { isOnlyOneUserSelection?: boolean; onlyOneUserIsObject?: boolean },
  ): UsersSelectionColumnConfig => ({
    popupTitle: title,
    isOnlyOneUserSelection: !!config?.isOnlyOneUserSelection,
    onlyOneUserIsObject: !!config?.onlyOneUserIsObject,
    onChange: ({ rowData, columnName, value }) => saveEditedRow(rowData, true, columnName, value),
  }), [saveEditedRow]);

  const columns = useMemo<ColumnConfig[]>(() => [
    { name: COLUMN_NAMES.PLANT_NAME, title: t('plants.plantsName'), flex: 2, wrapText: true, editable: true },
    { name: COLUMN_NAMES.WKZ_NUMBER, title: t('plants.wkz'), flex: 1.3 },
    {
      name: COLUMN_NAMES.AUTHORIZED_USER_CODES,
      title: t('plants.authorizedUsers'),
      flex: 3,
      wrapText: true,
      usersSelection: buildUsersSelection(t('plants.authorizedUsers')),
    },
    {
      name: COLUMN_NAMES.LANGUAGE,
      title: t('plants.language'),
      flex: 1.9,
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: Object.keys(languagesForm),
        formatValue: (value: string) => langFormFormatter(value, languagesForm, lang),
      },
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, languagesForm, lang),
    },
    { name: COLUMN_NAMES.MAILBOX, title: t('plants.mailbox'), flex: 2.5, wrapText: true, editable: true },
    {
      name: COLUMN_NAMES.CKD,
      title: t('plants.ckd'),
      flex: 1,
      editable: true,
      align: ALIGN.CENTER,
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      filterType: FILTER_TYPES.SET,
      filterValueGetter: ({ value }: { value: unknown }) => (value ? 'YES' : 'NO'),
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, formYESNO, lang),
    },
    {
      name: COLUMN_NAMES.QMB_APPROVER,
      title: t('plants.qmbApprover'),
      flex: 2,
      usersSelection: buildUsersSelection(t('plants.qmbApprover'), { isOnlyOneUserSelection: true, onlyOneUserIsObject: false }),
    },
    {
      name: COLUMN_NAMES.SELECTION_MODE,
      title: t('plants.selectionMode'),
      flex: 1.6,
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: Object.keys(SELECTION_MODES_FORM),
        formatValue: (value: string) => langFormFormatter(value, SELECTION_MODES_FORM, lang),
      },
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, SELECTION_MODES_FORM, lang),
    },
    {
      name: COLUMN_NAMES.WORKFLOW_TYPE,
      title: t('plants.workflowType'),
      flex: 1.5,
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: Object.keys(WORKFLOW_TYPE_FORM),
        formatValue: (value: string) => langFormFormatter(value, WORKFLOW_TYPE_FORM, lang),
      },
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, WORKFLOW_TYPE_FORM, lang),
    },
    {
      name: COLUMN_NAMES.ACTIVE,
      title: t('plants.active'),
      flex: 0.8,
      editable: true,
      align: ALIGN.CENTER,
      cellRenderer: CELL_RENDERER_TYPES.CHECKBOX,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      filterType: FILTER_TYPES.SET,
      filterValueGetter: ({ value }: { value: unknown }) => (value ? 'YES' : 'NO'),
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, formYESNO, lang),
    },
  ], [t, lang, languagesForm]);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('administration.plants'));
  }, [t, lang]);

  const handleFilterChange = (filter: string, multiple: string | null, newPlants?: Plant[]) => {
    const allPlants = newPlants || plants;

    if (multiple) {
      setResultsPlants(allPlants.filter((elem) => elem[multiple] === filter));
      setFilterSelected(filter);

      return;
    }

    switch (filter) {
      case 'all':
        setResultsPlants(allPlants);
        break;
      case 'withQMBApprover':
        setResultsPlants(allPlants.filter((elem) => elem.qmbApprover !== ''));
        break;
      case 'ckd':
        setResultsPlants(allPlants.filter((elem) => elem.ckd === true));
        break;
      default:
        setResultsPlants(allPlants.filter((elem) => elem[filter] === true));
        break;
    }
    setFilterSelected(filter);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      administrationServices.getPlants(),
      languageService.getAvailableLanguages(),
    ])
      .then((res) => {
        const [resPlants, languages] = res;
        setPlants(resPlants.plants);
        setResultsPlants(resPlants.plants);
        setLanguages(getLanguageTranslations(languages.languages));
        setInitialLoading(false);
      })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteSecteds = () => {
    setLoading(true);
    setPopupOpen('');
    administrationServices.deletePlant(selection)
      .then(() => null) // needed for unit test
      .catch((error) => {
        if (error.code === errorTypes.ONLY_ONE_PLANT) {
          const errorData: any = Object.entries(error.data).map(([key, values]: any) => [key, ...values]);
          setErrorPlantList(errorData);

          return;
        }
        addToast(error.message, 'ERROR');
        setLoading(false);
      })
      .finally(getPlants);
  };

  const formattedErrorList = errorPlantList?.map(([plantName, ...productGroupName]) => (
    <p key={plantName}>
      {t(`administration.onePlantDialog.message${productGroupName.length > 1 ? 'Multiple' : ''}`,
        { plantName, productGroupName: productGroupName.join(', ') })}
    </p>
  ));

  return (
    <>
      <div className={styles.Plants}>
        <AccordionPanel
          title={`${t('administration.availablePlants')} ${resultsPlants ? `(${resultsPlants.length})` : ''}`}
        >
          {initialLoading && (<Spinner isVisible isMask />)}
          {!initialLoading && (
            <>
              <div className={styles.ButtonsTable}>
                <div className={styles.ButtonsContentTableLeft}>
                  {availableFilters.map((f) => (
                    <Button
                      key={f}
                      id={`filter-${f}`}
                      active={filterSelected === f}
                      handleClick={() => handleFilterChange(f, null)}
                      buttonStyle="GreyFilter"
                      btnText={t(`plants.${f}`)}
                    />
                  ))}
                  <ButtonChipMultiple
                    id="filter-language"
                    handleClick={(key) => handleFilterChange(key, 'language')}
                    active={filterSelected}
                    buttonStyle="GreyFilter"
                    options={Object.keys(languagesForm)}
                    translateFunction={(key) => t(`languages.${key}`)}
                  />
                  <ButtonChipMultiple
                    id="filter-workflow-type"
                    handleClick={(key) => handleFilterChange(key, 'workflowType')}
                    active={filterSelected}
                    buttonStyle="GreyFilter"
                    options={Object.keys(WORKFLOW_TYPE_FORM)}
                    translateFunction={(key) => t(`plants.${key}`)}
                  />
                  <ButtonChipMultiple
                    id="filter-selection-mode"
                    handleClick={(key) => handleFilterChange(key, 'selectionMode')}
                    active={filterSelected}
                    buttonStyle="GreyFilter"
                    options={Object.keys(SELECTION_MODES_FORM)}
                    translateFunction={(key) => t(`plants.${key}`)}
                  />
                </div>
                <div className={styles.ButtonsContentTableRight}>
                  <IcoButtonSimple
                    disabled={!(selection.length > 0)}
                    id="btn-delete-plant"
                    icon="icon-muelleimer-loeschen"
                    tooltipText={t('software.delete') || ''}
                    handleClick={() => setPopupOpen('DELETION')}
                  />
                  <SimpleButton
                    id="btn-add-plant"
                    color="primary"
                    buttonText={t('administration.add')}
                    handleClick={() => setPopupOpen('CREATION')}
                  />
                </div>
              </div>

              <TableListEditableNew
                isFiltering
                hasCheckbox
                list={resultsPlants as (Plant & Record<string, unknown>)[]}
                onCellValueChanged={(event) => {
                  if (!event.data || event.newValue === event.oldValue) return;
                  const isEditableInput = EDITABLE_TEXT_INPUT_COLUMNS.includes(event.colDef?.field as string);
                  const saved = saveEditedRow(event.data, !isEditableInput);
                  if (!saved) event.node.setDataValue(event.colDef.field!, event.oldValue);
                }}
                columns={columns}
                selection={selection}
                onSelectionChange={(rows) => setSelection(rows as string[])}
                loading={loading}
                columnId={COLUMN_NAMES.WKZ_NUMBER}
              />
            </>
          )}
        </AccordionPanel>
      </div>
      {popupOpen === 'DELETION' && (
        <ConfirmDialog
          reject={() => setPopupOpen('')}
          confirm={handleDeleteSecteds}
          title={t('dialog.deletePlants.title')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.remove')}
        >
          <p className={styles.RemovePlantsMessage}>
            {`${t(`dialog.deletePlants.${currentSelection.length === 1 ? 'messageSingular' : 'message'}`)}: `}
            <b>{currentSelection.map((ms) => `${ms?.plantName} (${ms?.wkzNumber})`).join(', ')}</b>
          </p>
        </ConfirmDialog>
      )}
      {popupOpen === 'CREATION' && (
        <DialogPlants
          title={t('administration.createPlant')}
          close={() => setPopupOpen('')}
          reloadPlants={getPlants}
        />
      )}
      {showOnlyOnePlantDialog && (
        <ConfirmDialog
          title={t('administration.onePlantDialog.title')}
          message={formattedErrorList}
          confirmBtnText={t('dialog.ok')}
          close={() => setErrorPlantList([])}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  languagesForm: getLanguagesForm(state),
  plants: getPlantsForm(state),
});

const actionCreators = {
  setLanguages: setLanguagesForm,
  setPlants: setPlantsForm,
};

export default connect(mapStateToProps, actionCreators)(Plants);
